import React from 'react';
import { X, Download, Calendar, ArrowRight } from 'lucide-react';
import { useContentPlanStore } from '../../../stores/contentPlanStore';
import { sanitizeHtml } from '../../../utils/sanitize';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface SavedPlanModalProps {
  planId: string;
  onClose: () => void;
}

export default function SavedPlanModal({ planId, onClose }: SavedPlanModalProps) {
  const plan = useContentPlanStore((state) => state.getPlan(planId));
  const navigate = useNavigate();

  if (!plan) return null;

  const handleDownload = () => {
    const sanitizedContent = sanitizeHtml(plan.content);
    const blob = new Blob([sanitizedContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plano-de-conteudo.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Plano baixado com sucesso!');
  };

  const handleAddToCalendar = () => {
    navigate('/dashboard/calendario', { 
      state: { showImportModal: true, selectedPlanId: planId }
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white p-4 border-b border-gray-200 flex justify-between items-center z-10">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <span className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
            </span>
            {plan.title || 'Plano de Conteúdo'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div 
            className="prose max-w-none plan-content"
            dangerouslySetInnerHTML={{ 
              __html: sanitizeHtml(plan.content)
            }} 
          />
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Criado em {new Date(plan.createdAt).toLocaleDateString('pt-BR')}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Baixar HTML
            </button>
            <button
              onClick={handleAddToCalendar}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Adicionar ao Calendário
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}